import React, { useRef, useState } from 'react';
import {
    Dialog,
    DialogPanel,
    DialogTitle,
    DialogBackdrop,
} from '@headlessui/react';
import { Plan, prepare } from '@photostat/experience-package';
import useAuthenticatedApi from '../../Authentication/components/hooks/useAuthenticatedApi';
import useUserOrg from '../../Authentication/components/hooks/useUserOrg';
import { API_URL } from '../../services/api';
import {
    createTransfer,
    downloadBytes,
    errorMessage,
    executeTransfer,
    exportPackage,
    hashBytes,
    readSelection,
} from './client';
interface Props {
    mode: 'import' | 'export';
    activationId: number;
    revision: number;
    definition: any;
    dirty: boolean;
    onClose: () => void;
    onSaved: (receipt: any) => void;
}
export default function ExperiencePackageDialog({
    mode,
    activationId,
    revision,
    definition,
    dirty,
    onClose,
    onSaved,
}: Props) {
    const api = useAuthenticatedApi(),
        { orgId } = useUserOrg();
    const route = `/${orgId}/experiences/${activationId}`;
    const [busy, setBusy] = useState(false),
        [error, setError] = useState(''),
        [progress, setProgress] = useState('');
    const [prepared, setPrepared] = useState<Plan | null>(null),
        [transfer, setTransfer] = useState<any>(null);
    const [includeFiles, setIncludeFiles] = useState(true),
        [draft, setDraft] = useState(false);
    const folder = useRef<HTMLInputElement>(null);
    const analyze = async (files: FileList | null) => {
        if (!files) return;
        setBusy(true);
        setError('');
        setTransfer(null);
        setPrepared(null);
        setProgress('Reading package and checking files…');
        try {
            const bundle = await readSelection(files);
            const plan = await prepare(
                bundle.definition,
                bundle.files,
                hashBytes,
            );
            const lock = bundle.lock;
            const same =
                lock?.environment === API_URL &&
                Number(lock?.orgId) === Number(orgId) &&
                Number(lock?.activationId) === Number(activationId);
            const base =
                same && Number.isInteger(lock.baseRevision)
                    ? lock.baseRevision
                    : revision;
            const result = await createTransfer(api, route, plan, base);
            setPrepared(plan);
            setTransfer(result);
            setProgress('');
        } catch (e) {
            setError(errorMessage(e));
            setProgress('');
        } finally {
            setBusy(false);
        }
    };
    const save = async () => {
        if (!prepared || !transfer) return;
        setBusy(true);
        setError('');
        try {
            const receipt = await executeTransfer(
                api,
                route,
                transfer,
                prepared.blobs,
                setProgress,
            );
            onSaved(receipt);
            onClose();
        } catch (e) {
            setError(errorMessage(e));
            setProgress('Completed uploads are retained. Retry to continue.');
        } finally {
            setBusy(false);
        }
    };
    const download = async () => {
        setBusy(true);
        setError('');
        setProgress('Preparing download…');
        try {
            const bytes = await exportPackage(
                api,
                route,
                Number(orgId),
                activationId,
                includeFiles,
                setProgress,
                draft ? { definition, baseRevision: revision } : undefined,
            );
            downloadBytes(
                bytes,
                `experience-${activationId}${draft ? '-draft' : ''}.photostat.zip`,
            );
            setProgress('Package downloaded.');
        } catch (e) {
            setError(errorMessage(e));
            setProgress('');
        } finally {
            setBusy(false);
        }
    };
    const uploads =
        transfer?.assets.filter((a: any) => a.status === 'upload') ?? [];
    return (
        <Dialog
            open
            onClose={() => {
                if (!busy) onClose();
            }}
            className="relative z-[10000]"
        >
            <DialogBackdrop className="fixed inset-0 bg-black/50" />
            <div className="fixed inset-0 flex items-center justify-center p-6">
                <DialogPanel className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 text-gray-900 shadow-xl">
                    <DialogTitle className="text-xl font-semibold">
                        {mode === 'import'
                            ? 'Import experience package'
                            : 'Download experience package'}
                    </DialogTitle>
                    <p className="mt-2 text-sm text-gray-600">
                        Activation #{activationId} · Organization #{orgId} ·
                        Saved version {revision || 'none'}
                    </p>
                    {mode === 'import' ? (
                        <>
                            <p className="my-4 text-sm">
                                Choose a ZIP, a folder, or an experience JSON
                                with supporting files. Existing cloud files are
                                reused.
                            </p>
                            {dirty && (
                                <p className="mb-3 text-sm text-amber-800">
                                    Importing will replace your unsaved editor
                                    changes after the package saves
                                    successfully.
                                </p>
                            )}
                            <label className="block text-sm font-medium">
                                Package or files
                                <input
                                    aria-label="Package or files"
                                    type="file"
                                    multiple
                                    disabled={busy}
                                    className="my-2 block w-full"
                                    onChange={(e) =>
                                        void analyze(e.target.files)
                                    }
                                />
                            </label>
                            <input
                                ref={folder}
                                type="file"
                                multiple
                                className="hidden"
                                onChange={(e) => void analyze(e.target.files)}
                                {...({
                                    webkitdirectory: '',
                                    directory: '',
                                } as any)}
                            />
                            <button
                                disabled={busy}
                                type="button"
                                className="rounded border px-3 py-2 text-sm"
                                onClick={() => folder.current?.click()}
                            >
                                Choose folder
                            </button>
                            {prepared && transfer && (
                                <div className="my-4">
                                    <p className="font-medium">
                                        {prepared.definition.screens.length}{' '}
                                        screens · {uploads.length} files to
                                        upload ·{' '}
                                        {transfer.assets.length -
                                            uploads.length}{' '}
                                        reused · {prepared.remoteUrls.length}{' '}
                                        cloud URLs kept
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        {(
                                            uploads.reduce(
                                                (n: number, a: any) =>
                                                    n + a.bytes,
                                                0,
                                            ) / 1048576
                                        ).toFixed(2)}{' '}
                                        MiB to upload. The activation changes
                                        only after every file is ready.
                                    </p>
                                    <ul className="mt-3 max-h-56 overflow-y-auto divide-y">
                                        {transfer.assets.map((a: any) => (
                                            <li
                                                key={a.sha256}
                                                className="flex justify-between py-2 text-sm"
                                            >
                                                <span>{a.name}</span>
                                                <span>
                                                    {a.status === 'reuse'
                                                        ? 'Reuse'
                                                        : 'Upload'}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="my-4 space-y-3 text-sm">
                            <p>
                                Download the saved experience with cloud
                                identity information, then edit locally and
                                import it again.
                            </p>
                            <label className="flex gap-2">
                                <input
                                    type="checkbox"
                                    checked={includeFiles}
                                    disabled={busy}
                                    onChange={(e) =>
                                        setIncludeFiles(e.target.checked)
                                    }
                                />
                                Include files for offline editing
                            </label>
                            {dirty && (
                                <label className="flex gap-2">
                                    <input
                                        type="checkbox"
                                        checked={draft}
                                        disabled={busy}
                                        onChange={(e) =>
                                            setDraft(e.target.checked)
                                        }
                                    />
                                    Export my unsaved local draft instead of the
                                    saved version
                                </label>
                            )}
                            <p className="text-gray-600">
                                Unchanged files will reuse their library URLs on
                                the next import. Links-only packages leave
                                remote assets in the cloud.
                            </p>
                        </div>
                    )}
                    {error && (
                        <p
                            role="alert"
                            className="my-4 whitespace-pre-wrap rounded bg-red-50 p-3 text-sm text-red-800"
                        >
                            {error}
                        </p>
                    )}
                    <p
                        aria-live="polite"
                        role="status"
                        className="my-3 text-sm"
                    >
                        {progress}
                    </p>
                    <div className="sticky bottom-0 mt-5 flex justify-end gap-3 bg-white pt-3">
                        <button
                            className="rounded border px-4 py-2"
                            disabled={busy}
                            onClick={onClose}
                        >
                            Close
                        </button>
                        <button
                            className="rounded bg-black px-4 py-2 font-medium text-white disabled:opacity-40"
                            disabled={busy || (mode === 'import' && !transfer)}
                            onClick={() =>
                                void (mode === 'import' ? save() : download())
                            }
                        >
                            {busy
                                ? 'Working…'
                                : mode === 'import'
                                  ? error
                                      ? 'Retry upload & save'
                                      : 'Upload & save experience'
                                  : 'Download package'}
                        </button>
                    </div>
                </DialogPanel>
            </div>
        </Dialog>
    );
}
